'use client'

import * as React from 'react'
import { Drawer as DrawerPrimitive, Handle } from 'vaul'

import {
  Drawer as OriginDrawer,
  DrawerClose,
  DrawerContent as OriginDrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay as OriginDrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
} from '../ui/drawer'
import { cn } from '../../lib/utils'
import { useState } from 'react'
import { ChevronDownIcon } from 'lucide-react'

const Drawer = ({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => {
  const [dragStartY, setDragStartY] = useState<number | null>(null)

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches?.[0]) {
      setDragStartY(e.touches[0].clientY)
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (dragStartY !== null && e.touches?.[0]) {
      const currentY = e.touches[0].clientY
      const dragDistance = currentY - dragStartY
      const viewportHeight = window.innerHeight
      const percentageDragged = dragDistance / viewportHeight

      if (percentageDragged > 0.5 && props?.onOpenChange) {
        props.onOpenChange(false)
        setDragStartY(null)
      }
    }
  }

  const handleTouchEnd = () => {
    setDragStartY(null)
  }

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <OriginDrawer {...props} />
    </div>
  )
}

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content> & { showHandler?: boolean },
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    showHandler?: boolean
  }
>(({ className, children, showHandler, ...props }, ref) => (
  <OriginDrawerContent
    // No top: env(safe-area-inset-top). It is only non-zero in an edge-to-edge
    // webview (the Oddle Eats app), where the app already offsets the webview
    // below the status bar via its own native header — so this pushed the
    // drawer down by a status-bar-height and left a gap at the top / cut the
    // drawer short of full height. In a normal browser the inset is 0 anyway.
    style={{
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
    }}
    className={cn('z-[9999] mt-0 rounded-none', className)}
    ref={ref}
    {...props}
  >
    {showHandler && (
      <DrawerClose className="flex h-7 w-full justify-center py-1">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="9"
          viewBox="0 0 14 9"
          fill="none"
        >
          <path
            d="M8.30145 7.83419C7.51584 8.64234 6.21794 8.64204 5.43269 7.83354L0.450166 2.70345C-0.151023 2.08446 -0.150054 1.09932 0.452354 0.481517C1.0782 -0.160328 2.11022 -0.159543 2.73509 0.483253L5.43359 3.25917C6.21861 4.06671 7.5153 4.06712 8.30083 3.26009L11.005 0.481901C11.6301 -0.160279 12.6616 -0.160733 13.2873 0.480896C13.8899 1.09897 13.8907 2.0846 13.289 2.70359L8.30145 7.83419Z"
            fill="white"
          />
        </svg>
      </DrawerClose>
    )}
    {children}
  </OriginDrawerContent>
)) as React.ForwardRefExoticComponent<
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    showHandler?: boolean
  } & React.RefAttributes<React.ElementRef<typeof DrawerPrimitive.Content>>
>
DrawerContent.displayName = 'DrawerContent'

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <OriginDrawerOverlay
    ref={ref}
    className={cn('z-[9999]', className)}
    {...props}
  />
)) as React.ForwardRefExoticComponent<
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> &
    React.RefAttributes<React.ElementRef<typeof DrawerPrimitive.Overlay>>
>
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
  Handle as DrawerHandle,
}
